import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "@/services/apiClient";
import { staffUserService } from "@/services/staffUserService";
import {
  getStaffIdentity,
  hasPermission,
  STAFF_ACCOUNT_TYPE,
  STAFF_PERMISSION,
} from "@/utils/staffAuth";
import ToastHelper from "@/utils/toastHelper";
import { Switch } from "@/components/ui/switch";
import DataListPage from "./DataListPage";

const map = (x) => ({
  id: `T-${x.trainerId}`,
            render: (r) => (
              <span
                className={`wc-badge ${r.raw.adminCredentialId && r.raw.isCredentialActive && !r.raw.isCredentialDisabled ? "bg-emerald-50 text-emerald-700" : r.raw.adminCredentialId ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}
              >
                {r.portal}
              </span>
            ),
  name: `${x.firstName} ${x.lastName}`,
  email: x.email,
  qualification: x.qualification,
  status: x.isDisabled || x.isDeleted ? "Inactive" : "Active",
  portal: x.adminCredentialId
    ? x.isCredentialActive && !x.isCredentialDisabled
      ? "Active"
      : "Inactive"
    : "Not Created",
  classes: x.classCount,
  raw: x,
});

export default function AdminTrainers() {
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [params] = useSearchParams();
  const api = process.env.REACT_APP_API_URL.replace(/\/$/, "");
  const superAdmin =
    getStaffIdentity().accountTypeId === STAFF_ACCOUNT_TYPE.SUPER_ADMIN;
  const canManageTrainers = hasPermission(STAFF_PERMISSION.TRAINER_MANAGE);
  const load = useCallback(
    () =>
      apiClient
        .get(`${api}/AdminTrainer`)
        .then((r) => setRows(r.data.map(map)))
        .catch(() => ToastHelper.error("Unable to load trainers")),
    [api],
  );
  useEffect(() => {
    load();
    if (superAdmin)
      staffUserService
        .availableTrainerCredentials()
        .then(setCredentials)
        .catch(() => {});
  }, [load, superAdmin]);
  useEffect(() => {
    const id = Number(params.get("trainerId") || 0);
    const row = rows.find((x) => x.raw.trainerId === id);
    if (row) setDialog({ mode: "view", item: { ...row.raw } });
  }, [params, rows]);
  const openAdd = () =>
    setDialog({
      mode: "add",
      item: {
        trainerId: 0,
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        qualification: "",
        isDeleted: false,
        isDisabled: false,
        editType: 0,
      },
    });
  const save = async () => {
    if (!dialog.item.firstName.trim() || !dialog.item.lastName.trim()) {
      ToastHelper.error("First name and last name are required");
      return;
    }
    try {
      const r =
        dialog.mode === "add"
          ? await apiClient.post(`${api}/AdminTrainer`, dialog.item)
          : await apiClient.put(
              `${api}/AdminTrainer/${dialog.item.trainerId}`,
              dialog.item,
            );
      const row = map(r.data);
      setRows((cur) =>
        dialog.mode === "add"
          ? [...cur, row]
          : cur.map((x) => (x.raw.trainerId === row.raw.trainerId ? row : x)),
      );
      setDialog(null);
      ToastHelper.success("Trainer saved successfully");
    } catch (e) {
      ToastHelper.apiError(e, "Unable to save trainer");
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete trainer ${row.name}?`)) return;
    try {
      await apiClient.delete(`${api}/AdminTrainer/${row.raw.trainerId}`);
      setRows((cur) =>
        cur.filter((x) => x.raw.trainerId !== row.raw.trainerId),
      );
      ToastHelper.success("Trainer deleted successfully");
    } catch (e) {
      ToastHelper.apiError(e, "Unable to delete trainer");
    }
  };
  const createPortal = async () => {
    const email = String(dialog.portalForm?.email || "").trim().toLowerCase();
    const duplicate = rows.some((row) => row.raw.trainerId !== dialog.item.trainerId && String(row.raw.email || "").trim().toLowerCase() === email);
    if (duplicate) {
      ToastHelper.error("This email address is already used by another trainer.");
      return;
    }
    try {
      await staffUserService.createTrainerPortal(
        dialog.item.trainerId,
        dialog.portalForm,
      );
      ToastHelper.success("Portal account created and linked");
      setDialog(null);
      await load();
    } catch (e) {
      ToastHelper.apiError(e, "Unable to create portal account");
    }
  };
  const linkPortal = async () => {
    try {
      await staffUserService.linkTrainerCredential(
        dialog.item.trainerId,
        Number(dialog.credentialId),
      );
      ToastHelper.success("Portal account linked");
      setDialog(null);
      await load();
    } catch (e) {
      ToastHelper.apiError(e, "Unable to link portal account");
    }
  };
  return (
    <>
      <DataListPage
        title="Trainers"
        subtitle="Manage trainer profiles and assignments"
        testidPrefix="trainers"
        rows={rows}
        addButton={canManageTrainers ? "+ Add trainer" : undefined}
        onAdd={canManageTrainers ? openAdd : undefined}
        actions={[
          {
            label: "View",
            fn: (r) => setDialog({ mode: "view", item: { ...r.raw } }),
          },
          {
            label: "Edit",
            fn: (r) => setDialog({ mode: "edit", item: { ...r.raw } }),
            hidden: !canManageTrainers,
          },
        ]}
        columns={[
          { key: "id", label: "ID" },
          {
            key: "name",
            label: "Name",
            render: (r) => <span className="font-semibold">{r.name}</span>,
          },
          { key: "email", label: "Email" },
          { key: "qualification", label: "Qualification" },
          {
            key: "status",
            label: "Status",
            render: (r) => (
              <span
                className={`wc-badge ${r.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
              >
                {r.status}
              </span>
            ),
          },
          {
            key: "portal",
            label: "Portal Access",
            render: (r) => (
              <span className="inline-flex items-center gap-2">
                <Switch
                  checked={Boolean(r.raw.adminCredentialId && r.raw.isCredentialActive && !r.raw.isCredentialDisabled)}
                  disabled
                  className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-rose-400"
                />
                <span className={`text-xs font-semibold ${r.portal === "Active" ? "text-emerald-700" : r.portal === "Inactive" ? "text-rose-700" : "text-slate-500"}`}>
                  {r.portal}
                </span>
              </span>
            ),
          },
          { key: "classes", label: "Classes", align: "right" },
        ]}
      />
      {dialog && (
        <TrainerDialog
          dialog={dialog}
          setDialog={setDialog}
          save={save}
          superAdmin={superAdmin}
          credentials={credentials}
          createPortal={createPortal}
          linkPortal={linkPortal}
        />
      )}
    </>
  );
}

function TrainerDialog({
  dialog,
  setDialog,
  save,
  superAdmin,
  credentials,
  createPortal,
  linkPortal,
}) {
  const editing = dialog.mode !== "view";
  const change = (k, v) =>
    setDialog((d) => ({ ...d, item: { ...d.item, [k]: v } }));
  const startCreate = () =>
    setDialog((d) => ({
      ...d,
      portalMode: "create",
      portalForm: {
        userName: "",
        email: d.item.email || "",
        password: "",
        confirmPassword: "",
        isTestAccount: false,
        isActive: true,
      },
    }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white shadow-2xl">
        <div className="flex justify-between border-b px-6 py-4">
          <h2 className="text-xl font-extrabold text-[#0A2540]">
            {dialog.mode === "add"
              ? "Add trainer"
              : dialog.mode === "edit"
                ? "Edit trainer"
                : "Trainer details"}
          </h2>
          <button onClick={() => setDialog(null)} className="text-2xl">
            ×
          </button>
        </div>
        <div className="grid gap-4 p-6 sm:grid-cols-2">
          {[
            ["firstName", "First name"],
            ["lastName", "Last name"],
            ["email", "Email"],
            ["phoneNumber", "Phone number"],
            ["qualification", "Qualification"],
          ].map(([k, l]) => (
            <label
              key={k}
              className={k === "qualification" ? "sm:col-span-2" : ""}
            >
              <span className="wc-label">{l}</span>
              <input
                className="wc-input"
                disabled={!editing}
                value={dialog.item[k] || ""}
                onChange={(e) => change(k, e.target.value)}
              />
            </label>
          ))}
          <label className="flex items-center gap-3">
            <Switch
              disabled={!editing}
              checked={!dialog.item.isDisabled}
              className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-rose-400"
              onCheckedChange={(checked) => change("isDisabled", !checked)}
            />
            <span className="text-sm font-semibold">
              {dialog.item.isDisabled ? "Inactive" : "Active"}
            </span>
          </label>
        </div>
        {dialog.mode !== "add" && (
          <div className="mx-6 mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-3 text-xs font-extrabold uppercase tracking-wider text-slate-500">
              Portal Access
            </h3>
            {dialog.item.adminCredentialId ? (
              <div className="grid gap-2 text-sm sm:grid-cols-2">
                <p>
                  <b>Portal Account:</b> {dialog.item.portalUserName}
                </p>
                <p>
                  <b>Account Type:</b> {dialog.item.portalAccountTypeName}
                </p>
                <p>
                  <b>Login Status:</b>{" "}
                  {dialog.item.isCredentialActive &&
                  !dialog.item.isCredentialDisabled
                    ? "Active"
                    : "Inactive"}
                </p>
                <p>
                  <b>Test Account:</b>{" "}
                  {dialog.item.isTestAccount ? "Yes" : "No"}
                </p>
              </div>
            ) : (
              <>
                <p className="mb-3 text-sm text-slate-600">
                  No portal login has been created for this trainer.
                </p>
                {dialog.portalMode === "create" && (
                  <PortalForm
                    dialog={dialog}
                    setDialog={setDialog}
                    submit={createPortal}
                  />
                )}{" "}
                {dialog.portalMode === "link" && (
                  <div className="space-y-3">
                    <select
                      className="wc-input"
                      value={dialog.credentialId || ""}
                      onChange={(e) =>
                        setDialog((d) => ({
                          ...d,
                          credentialId: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Trainer credential</option>
                      {credentials.map((x) => (
                        <option
                          key={x.adminCredentialId}
                          value={x.adminCredentialId}
                        >
                          {x.userName} — {x.email}
                        </option>
                      ))}
                    </select>
                    <button className="wc-btn-primary" onClick={linkPortal}>
                      Link Account
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button className="wc-btn-outline" onClick={() => setDialog(null)}>
            Close
          </button>
          {editing && (
            <button className="wc-btn-primary" onClick={save}>
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalForm({ dialog, setDialog, submit }) {
  const change = (k, v) =>
    setDialog((d) => ({ ...d, portalForm: { ...d.portalForm, [k]: v } }));
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {[
        ["userName", "Username", "text"],
        ["email", "Login Email", "email"],
        ["password", "Password", "password"],
        ["confirmPassword", "Confirm Password", "password"],
      ].map(([k, l, t]) => (
        <label key={k}>
          <span className="wc-label">{l} *</span>
          <input
            className="wc-input"
            type={t}
            value={dialog.portalForm[k]}
            onChange={(e) => change(k, e.target.value)}
          />
        </label>
      ))}
      <label className="flex items-center gap-3">
        <Switch
          checked={dialog.portalForm.isActive}
          onCheckedChange={(checked) => change("isActive", checked)}
        />
        <span className="text-sm font-semibold">
          {dialog.portalForm.isActive ? "Active" : "Inactive"}
        </span>
      </label>
      <label className="flex items-center gap-2">
          <Switch
            checked={dialog.portalForm.isTestAccount}
            onCheckedChange={(checked) => change("isTestAccount", checked)}
          />
          <span className="text-sm font-semibold">Test Account</span>
      </label>
      <button className="wc-btn-primary sm:col-span-2" onClick={submit}>
        Create and Link
      </button>
    </div>
  );
}
